/**
 * Client for the TITAN assistant backend (prod/chat/router.py) -- the same
 * endpoint and data TITAN_dev's AI Assistant uses, ported from its
 * src/pages/Chat/chatApi.ts.
 *
 * Every call carries the signed-in user's Firebase ID token. POST /v1/chat
 * streams Server-Sent Events, which we parse off a fetch ReadableStream; the
 * Anthropic key stays on the server and is never seen by this app.
 */
import firebase from 'firebase/app';
import 'firebase/auth';

const API = 'https://api.missouri-titan.com';

export interface ChatEvent {
    type: 'meta' | 'text' | 'tool' | 'chart' | 'snapshots' | 'map_link' | 'done' | 'error';
    conversation_id?: string;
    text?: string;
    name?: string;
    message?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    spec?: any;
    items?: {title: string; image_url: string}[];
}

export interface Conversation {
    id: string;
    title: string;
    updated_at: string;
}

export interface StoredMessage {
    role: 'user' | 'assistant';
    content: string;
}

/** Human-readable names for the tools the assistant reports consulting. */
export const TOOL_LABELS: Record<string, string> = {
    get_active_incidents: 'active incidents',
    get_incident_queues: 'incident queues',
    get_traffic_conditions: 'traffic speeds',
    get_corridor_risk: 'corridor risk',
    get_weather_and_road_conditions: 'weather',
    get_flood_status: 'flooding',
    get_recent_summaries: 'recent summaries',
};

async function authHeader(forceRefresh = false): Promise<Record<string, string>> {
    const user = firebase.auth().currentUser;
    if (!user) {
        throw new Error('Not signed in');
    }
    return {Authorization: `Bearer ${await user.getIdToken(forceRefresh)}`};
}

/** POST /v1/chat, yielding parsed SSE events as they arrive. */
export async function* streamChat(
    message: string,
    conversationId: string | null
): AsyncGenerator<ChatEvent> {
    const post = async (forceRefresh: boolean) => fetch(`${API}/v1/chat`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', ...(await authHeader(forceRefresh))},
        body: JSON.stringify({message, conversation_id: conversationId}),
    });

    let response = await post(false);
    // A cached ID token can predate a claim change (admin granted after the
    // last sign-in), so a rejection is worth one retry on a fresh token.
    if (response.status === 401 || response.status === 403) {
        response = await post(true);
    }

    if (!response.ok || !response.body) {
        const detail = response.status === 429
            ? 'You have reached the hourly message limit. Try again shortly.'
            : response.status === 401 || response.status === 403
                ? 'Could not verify your session. Sign out and back in.'
                : `The assistant is unavailable (${response.status}).`;
        yield {type: 'error', message: detail};
        return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    for (;;) {
        const {done, value} = await reader.read();
        if (done) {
            break;
        }
        buffer += decoder.decode(value, {stream: true});

        // SSE frames are separated by a blank line; a frame may straddle reads.
        let split = buffer.indexOf('\n\n');
        while (split !== -1) {
            const frame = buffer.slice(0, split).trim();
            buffer = buffer.slice(split + 2);
            if (frame.startsWith('data:')) {
                try {
                    yield JSON.parse(frame.slice(5).trim()) as ChatEvent;
                } catch {
                    // A frame we cannot parse is not worth killing the stream.
                }
            }
            split = buffer.indexOf('\n\n');
        }
    }
}

export async function listConversations(): Promise<Conversation[]> {
    const res = await fetch(`${API}/v1/chat/conversations`, {headers: await authHeader()});
    if (!res.ok) {
        return [];
    }
    const data = await res.json();
    return data.conversations ?? data ?? [];
}

export async function getMessages(conversationId: string): Promise<StoredMessage[]> {
    const res = await fetch(
        `${API}/v1/chat/conversations/${conversationId}/messages`,
        {headers: await authHeader()}
    );
    if (!res.ok) {
        return [];
    }
    const data = await res.json();
    return data.messages ?? data ?? [];
}

export async function deleteConversation(conversationId: string): Promise<void> {
    await fetch(`${API}/v1/chat/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: await authHeader(),
    });
}
